import React from 'react'
import {
    Button,
    getMaxLengthErrorMessage,
    getMinLengthErrorMessage,
    InputPassword,
} from '@dostavkee/web-ui'
import { useNavigate } from '@tanstack/react-router'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { useAuthProfileSetter, useResetPassword } from '@/entities/auth'
import { getUsersProfile, type UserResetPasswordVerifyRequest } from '@/shared/api'

import {
    hasDigitsOrSymbols,
    hasLatinLetters,
    MIN_PASSWORD_LENGTH,
    ROLES,
} from '../../model/constants'
import styles from './set-password-form.module.scss'

export interface ResetPasswordFormValues
    extends Omit<UserResetPasswordVerifyRequest, 'code' | 'code_uuid'> {}

interface SetPasswordFormProps {
    code: string
    code_uuid: string
    isReset?: boolean
}

export const SetPasswordForm: React.FC<SetPasswordFormProps> = ({
    code,
    code_uuid,
    isReset = false,
}) => {
    const { resetPasswordAsync } = useResetPassword({ isReset })
    const { profileSetter } = useAuthProfileSetter()
    const navigate = useNavigate()

    const {
        control,
        formState: { isSubmitting },
        handleSubmit,
    } = useForm()

    const onSubmit = handleSubmit((data) =>
        resetPasswordAsync(
            {
                data: {
                    password: data.password,
                    code: code!,
                    code_uuid: code_uuid!,
                },
            },
            {
                onSuccess: async () => {
                    const response = await getUsersProfile()

                    profileSetter(response?.user)

                    const role = response?.user?.role

                    if (!role) {
                        toast.error('Ошибка при получении профиля')

                        return
                    }

                    switch (role) {
                        case ROLES.superManager: {
                            navigate({ to: '/recruitment', search: { page: 1 } })
                            break
                        }
                        case ROLES.recruiter: {
                            navigate({ to: '/recruitment', search: { page: 1 } })
                            break
                        }
                        default: {
                            navigate({ to: '/support' })
                            break
                        }
                    }
                },
            }
        )
    )

    return (
        <form className={styles['set-password-form']} onSubmit={onSubmit}>
            <header className={styles['set-password-form__header']}>
                <h1 className={styles['set-password-form__title']}>
                    {isReset ? 'Придумайте новый пароль' : 'Придумайте пароль'}
                </h1>
            </header>

            <div className={styles['set-password-form__body']}>
                <Controller
                    control={control}
                    name='password'
                    render={({ field, fieldState }) => (
                        <>
                            <InputPassword
                                autoComplete='off'
                                errorMessage={fieldState.error?.message}
                                id='password'
                                isInvalid={fieldState.invalid}
                                label='Пароль'
                                size='large'
                                data-testid={
                                    isReset
                                        ? 'reset-password-form__password-input'
                                        : 'set-password-form__password-input'
                                }
                                {...field}
                            />
                            <InputPassword.HintList>
                                <InputPassword.HintItem
                                    description='Минимум 8 символов'
                                    label='8+'
                                    value={field.value}
                                    isValid={
                                        typeof field.value === 'string' &&
                                        field.value.length >= MIN_PASSWORD_LENGTH
                                    }
                                />
                                <InputPassword.HintItem
                                    description='Только латинские буквы'
                                    isValid={hasLatinLetters(field.value)}
                                    label='ab'
                                    value={field.value}
                                />
                                <InputPassword.HintItem
                                    description='Минимум одна цифра или символ'
                                    isValid={hasDigitsOrSymbols(field.value)}
                                    label='1#'
                                    value={field.value}
                                />
                            </InputPassword.HintList>
                        </>
                    )}
                    rules={{
                        required: 'Обязательное поле',
                        minLength: {
                            value: MIN_PASSWORD_LENGTH,
                            message: getMinLengthErrorMessage(MIN_PASSWORD_LENGTH),
                        },
                        maxLength: {
                            value: 64,
                            message: getMaxLengthErrorMessage(64),
                        },
                        validate: {
                            containsLatinLetters: (value) =>
                                hasLatinLetters(value) || 'Только латинские буквы',
                            containsDigitsOrSymbols: (value) =>
                                hasDigitsOrSymbols(value) ||
                                'Должна быть хотя бы одна цифра или символ',
                        },
                    }}
                />

                <Button
                    fullWidth
                    isLoading={isSubmitting}
                    size='large'
                    type='submit'
                    data-testid={
                        isReset
                            ? 'reset-password-form__submit-button'
                            : 'set-password-form__submit-button'
                    }
                >
                    {isReset ? 'Сохранить' : 'Войти'}
                </Button>
            </div>
        </form>
    )
}
